import { createFileRoute } from "@tanstack/react-router"
import { User } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { useAuthStore } from "@/stores/auth-store"
import { useSites } from "@/lib/hooks/use-sites"
import { useMemo } from "react"

export const Route = createFileRoute("/_authenticated/account")({
  component: AccountPage,
})

function AccountPage() {
  const user = useAuthStore((s) => s.user)
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-2">
        <User className="h-5 w-5 text-[var(--color-accent-blue)]" />
        <div>
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Account
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Your profile and site permissions
          </p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Profile details */}
        <div className="rounded-[var(--radius-panel)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
            Profile
          </h2>
          <dl className="space-y-3">
            <div className="flex items-baseline gap-2">
              <dt className="w-28 shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
                First Name
              </dt>
              <dd className="text-sm text-[var(--color-text-primary)]">
                {user?.first_name || "—"}
              </dd>
            </div>
            <div className="flex items-baseline gap-2">
              <dt className="w-28 shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
                Last Name
              </dt>
              <dd className="text-sm text-[var(--color-text-primary)]">
                {user?.last_name || "—"}
              </dd>
            </div>
            <div className="flex items-baseline gap-2">
              <dt className="w-28 shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
                Email
              </dt>
              <dd className="text-sm text-[var(--color-text-primary)]">
                {user?.email || "—"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Site permissions */}
        <div className="rounded-[var(--radius-panel)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
            Site Permissions
          </h2>
          {user?.site_roles?.length ? (
            <div className="space-y-2">
              {user.site_roles.map((sr) => (
                <div
                  key={sr.site}
                  className="flex items-center justify-between rounded-[var(--radius-card)] bg-[var(--color-bg-tertiary)] px-4 py-2.5"
                >
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {siteNameMap.get(sr.site) ?? sr.site}
                  </span>
                  <Badge
                    variant={sr.role === "Editor" ? "default" : "secondary"}
                  >
                    {sr.role}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">
              No site permissions found.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
